/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { Auth } from 'aws-amplify'
import Cookies from 'js-cookie'
import { LoadingOverlay } from '../components/common'
import axios from 'axios'


const initialState = {
  user: null
}

export const UserContext = React.createContext(initialState)

export const UserProvider = ({ children }) => {
  const EXPIRES_TOKEN_COOKIE = '_ssoExpiresToken'
  const TOKEN_COOKIE = '_ssoToken'
  const REFRESH_TOKEN_COOKIE = '_ssoRefreshToken'
  const APP_COOKIE = '_ssoApp'
  const REDIRECT_URL_COOKIE = '_ssoRedirectUrl'
 
  const [authState, setAuthState] = useState('LOGIN')
  const [applications, setApplications] = useState(null)  
  const [app, setApp] = useState(null)
  const [isLoading, setLoading] = useState(true)
  const [user, setUser] = useState(null)

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  }) 

  const signIn = (email, password, rememberMe) => new Promise((resolve, reject) => {
    setUser(null)
    Auth.signIn(email, password).then(loggedUser => {
      if (loggedUser) {
        setUser(loggedUser)
        if (loggedUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setAuthState('NEW_PASSWORD_REQUIRED')
          resolve(false)
        } else {         
          // const expires = new Date()
          // expires.setMinutes(expires.getMinutes() + 55)     
          const expires = new Date(loggedUser.signInUserSession.idToken.payload.exp * 1000)      
          Cookies.set(TOKEN_COOKIE, loggedUser.signInUserSession.idToken.jwtToken, { expires: expires, domain: process.env.REACT_APP_DOMAIN })
          
          const expiresIn = (loggedUser.signInUserSession.idToken.payload.exp - loggedUser.signInUserSession.idToken.payload.auth_time)
          Cookies.set(EXPIRES_TOKEN_COOKIE, expiresIn, { expires: expires, domain: process.env.REACT_APP_DOMAIN })
          
          
          const refreshTokenExpires = new Date()
          refreshTokenExpires.setDate(refreshTokenExpires.getDate()+30)  
          Cookies.set(REFRESH_TOKEN_COOKIE, loggedUser.signInUserSession.refreshToken.token, { expires: refreshTokenExpires, domain: process.env.REACT_APP_DOMAIN })
          
          setAuthState('SIGNED_IN')
          Cookies.set('rememberMe', rememberMe, { expires: 365, domain: process.env.REACT_APP_DOMAIN })
          resolve(true)
        }
      }
    }).catch(error => {
      reject('Incorrect email or password. Please try again.')
    })
  })

  const changePassword = (newPassword) => new Promise((resolve, reject) => {
    Auth.completeNewPassword(user, newPassword).then(loggedUser => {
      setUser(loggedUser)
      const expires = new Date(loggedUser.signInUserSession.idToken.payload.exp * 1000)      
      Cookies.set(TOKEN_COOKIE, loggedUser.signInUserSession.idToken.jwtToken, { expires: expires, domain: process.env.REACT_APP_DOMAIN }) 
      
      const expiresIn = (loggedUser.signInUserSession.idToken.payload.exp - loggedUser.signInUserSession.idToken.payload.auth_time)
      Cookies.set(EXPIRES_TOKEN_COOKIE, expiresIn, { expires: expires, domain: process.env.REACT_APP_DOMAIN })

      
      const refreshTokenExpires = new Date()
      refreshTokenExpires.setDate(refreshTokenExpires.getDate()+30)  
      Cookies.set(REFRESH_TOKEN_COOKIE, loggedUser.signInUserSession.refreshToken.token, { expires: refreshTokenExpires, domain: process.env.REACT_APP_DOMAIN })     
               
      const headers = { 
        headers: { 
          'Authorization': loggedUser.signInUserSession.idToken.jwtToken, 
          'VLR-Authorization': btoa(JSON.stringify({ "id": app.id })) 
        }
      }
      
      api.post('/dashboard/notify-request', {}, headers).then((response) => {
        resolve()
      }).catch((error) => {
        console.error(error)
        resolve()
      })

    }).catch(error => {
      reject(manageError(error))
    })
  })

  const federatedSignIn = () => new Promise((resolve, reject) => {
    const expires = new Date(new Date().getTime() + 1 * 60 * 1000) //expires in 5 minutes 
    Cookies.set(APP_COOKIE, app.id, { expires: expires, domain: process.env.REACT_APP_DOMAIN })
    if (app.redirectUrl) {
      Cookies.set(REDIRECT_URL_COOKIE, app.redirectUrl, { expires: expires, domain: process.env.REACT_APP_DOMAIN })
    }

    Auth.federatedSignIn({provider: process.env.REACT_APP_AWS_AD_IDENTITY_PROVIDER}).then((result) => {
      console.log(result)
      resolve(result)
    }).catch(error => {
      reject(manageError(error))
    })
  })  

  const resetAuthState = () => {
    Cookies.remove(TOKEN_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
    Cookies.remove(EXPIRES_TOKEN_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
    Cookies.remove(REFRESH_TOKEN_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
    Cookies.remove(APP_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
    Cookies.remove(REDIRECT_URL_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
    setAuthState('LOGIN')
    setUser(null)
  }

  const sendRecoveryCode = username => new Promise((resolve, reject) => {
    Auth.forgotPassword(username).then(data => {
      resolve(data)
    }).catch(err => reject(manageError(err)))
  })

  const recoverPassword = (username, code, newPassword) => new Promise((resolve, reject) => {
    Auth.forgotPasswordSubmit(username, code, newPassword).then(data => {
      resetAuthState()
      resolve(data)
    }).catch(err => reject(manageError(err)))
  })

  
  const refreshSession = () => new Promise(async(resolve, reject) => {
    try {
      const cognitoUser = await Auth.currentSession()
      const expires = new Date(cognitoUser.getIdToken().payload.exp * 1000)      
      Cookies.set(TOKEN_COOKIE, cognitoUser.getIdToken().getJwtToken(), { expires: expires, domain: process.env.REACT_APP_DOMAIN })
      
      const expiresIn = (cognitoUser.getIdToken().payload.exp - cognitoUser.getIdToken().payload.auth_time)
      Cookies.set(EXPIRES_TOKEN_COOKIE, expiresIn, { expires: expires, domain: process.env.REACT_APP_DOMAIN })


      setAuthState('SIGNED_IN')
      resolve('Bearer ' + cognitoUser.getIdToken().getJwtToken())
    } catch (e) {
      reject('Cannot load session')  
    } 
  })

  const getCurrentUserInfo = () => new Promise(async(resolve, reject) => {
    try {
      const cognitoUser = await Auth.currentUserInfo()
      resolve(cognitoUser)
    } catch (e) {
      reject('Cannot load user')  
    } 
  })

  const signOut = async () => {
    try {
      setUser(null)
      Cookies.remove(TOKEN_COOKIE, { domain: process.env.REACT_APP_DOMAIN }) 
      Cookies.remove(EXPIRES_TOKEN_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
      Cookies.remove(REFRESH_TOKEN_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
      setAuthState('LOGIN')

      const expires = new Date(new Date().getTime() + 1 * 60 * 1000) //expires in 5 minutes 
      Cookies.set(APP_COOKIE, app.id, { expires: expires, domain: process.env.REACT_APP_DOMAIN })
      if (app.redirectUrl) {
        Cookies.set(REDIRECT_URL_COOKIE, app.redirectUrl, { expires: expires, domain: process.env.REACT_APP_DOMAIN })
      }
      
      await Auth.signOut({global: true})
    }
    catch (e) {
      console.log(e)
      throw e
    }
  }

  const createUser = (data, captChar) => {
    const headers = { 
      headers: { 
        'Authorization': captChar
      }
    }
 
    return new Promise((resolve, reject) => {
     api.post('/user/create', data, headers)
        .then((response) => {
          console.log(response)
          resolve(response)
        })
        .catch((error) => {
          reject(manageError(error))
        })
    })
  }

  const requestAccess = (data, captChar) => {
    const headers = { 
      headers: { 
        'Authorization': captChar
      }
    }

    return new Promise((resolve, reject) => {
      api.post('/user/request', data, headers)
        .then((response) => {
          console.log(response)
          resolve(response)
        })
        .catch((error) => {
          reject(manageError(error))
        })
    })
  }

  const redirect = (token) => new Promise((resolve) => {  
    const idToken = Cookies.get(TOKEN_COOKIE)
    const expiresToken = Cookies.get(EXPIRES_TOKEN_COOKIE)

    const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE)
    const idApp = btoa(JSON.stringify({ "id": app.id }))
    const env = process.env.NODE_ENV 
    const devUrl = process.env.REACT_APP_DEV_URL 


    let url = null;
    if (app.redirectUrl) {
      url = `${process.env.REACT_APP_API_URL}/redirect?p0=${idApp}&p1=${idToken}&p2=${token}&p3=${app.redirectUrl}&p4=${refreshToken}&p5=${expiresToken}`
    } else {
      if (env === 'development' && devUrl) {
        url = `${process.env.REACT_APP_API_URL}/redirect?p0=${idApp}&p1=${idToken}&p2=${token}&p3=${devUrl}&p4=${refreshToken}&p5=${expiresToken}`
      } else {
        url = `${process.env.REACT_APP_API_URL}/redirect?p0=${idApp}&p1=${idToken}&p2=${token}&p4=${refreshToken}&p5=${expiresToken}`
      }
    }
    
    window.location.replace(url)
    resolve()    
  })  

  const loadApp = () => new Promise((resolve, reject) => {
    try {
      if (applications) {
        const appIdCookie = Cookies.get(APP_COOKIE)
        const redirecUrlCookie = Cookies.get(REDIRECT_URL_COOKIE)
        Cookies.remove(APP_COOKIE, { domain: process.env.REACT_APP_DOMAIN })
        Cookies.remove(REDIRECT_URL_COOKIE, { domain: process.env.REACT_APP_DOMAIN })


        const query = new URLSearchParams(window.location.search)
        let ssoRedirectApp = applications.find(app => app.id === query.get('app'))
        if (!ssoRedirectApp) {
          if (appIdCookie) {
            ssoRedirectApp = applications.find(app => app.id === appIdCookie)
          }
        }
        if (!ssoRedirectApp) {
          reject('Cannot load app')
        } else {
          const env = process.env.NODE_ENV 
          const devUrl = process.env.REACT_APP_DEV_URL 
          if (env === 'development' && devUrl) {
            ssoRedirectApp.link = devUrl
          }            
          
          let redirectUrl = query.get('redirectUrl')
          if (!redirectUrl) {
            redirectUrl = redirecUrlCookie
          }
          
          if (redirectUrl) {
            ssoRedirectApp.redirectUrl = redirectUrl
            ssoRedirectApp.link = redirectUrl
          }
        
          setApp(ssoRedirectApp)
          resolve()
        }
      }
    } catch (e) {
      reject('Cannot load app')
    } 
  })
    
  useEffect(() => {

    const hydrate1 = new Promise((resolve, reject) => {
      api.post('/application/list', {}).then((response) => {          
        setApplications(response.data.body)
        const idToken = Cookies.get(TOKEN_COOKIE)
        if (idToken) {
          setAuthState('SIGNED_IN')
          resolve()
        } else {
          reject('No user session')
        }  
      }).catch((error) => {
        reject(error)
      })
    })

    hydrate1.then((values) => {
      console.log(values)
    }).catch(err => {
      console.log(err)
    }).finally(() => {
      setLoading(false)
    })
  }, [])
  
  
  const manageError = (error) => {   
    let message = JSON.stringify(error) 
    if (error.response) {    
      message = error.response.data.message ? error.response.data.message :  error.response.data
    } else  {
        message = error.message ? error.message : message 
    }
    return message
  }


  const renderStore = isLoading ?
    <LoadingOverlay visible={true} />
  :
    <UserContext.Provider value={{
      user,
      app, 
      loadApp,
      applications,
      authState,
      setAuthState,
      changePassword,
      sendRecoveryCode,
      recoverPassword,
      resetAuthState,
      federatedSignIn,
      createUser,
      requestAccess,
      signIn,
      signOut,
      refreshSession,
      getCurrentUserInfo,
      redirect,
      TOKEN_COOKIE
    }}>
      {children}
    </UserContext.Provider>

  return renderStore
}